import { ComponentType, CSSProperties, ReactNode, useEffect } from 'react';
import ReactModal from 'react-modal';

import { observer } from 'mobx-react-lite';

import { Stack, StackProps } from '@components/stack';
import { Text } from '@components/text';
import { useIsMobile } from '@hooks/use-is-mobile';
import { theme } from '@styles/theme';

import type { ModalType } from './modals-map';
import { useModalStore } from './use-modal';

import history from '../../routing/history';

ReactModal.setAppElement('#root');

type Props = {
  components: Record<ModalType, ComponentType<any>>;
};

export type ModalProps<T extends object = {}> = T & {
  closeModal(): void;
};

export const ModalComponent = observer(({ components }: Props) => {
  const modalStore = useModalStore();
  const modalType = modalStore.data?.modalType || null;
  const modalProps = modalStore.data?.modalProps || null;
  const isMobile = useIsMobile();

  useEffect(() => {
    if (modalType) {
      const unblock = history.block((tx) => {
        if (tx.action === 'POP') {
          modalStore.hide();
        } else {
          unblock();
          tx.retry();
        }
      });

      return unblock;
    }
  }, [modalStore, modalType]);

  let modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: theme.color.purpleTrans40,
      zIndex: 1000,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    } as CSSProperties,
    content: {
      top: 'auto',
      left: 'auto',
      right: 'auto',
      bottom: 'auto',
      width: '31.5rem',
      maxWidth: '98%',
      maxHeight: '98%',
      boxShadow: theme.shadow.high,
      borderRadius: '1rem',
      border: 'none',
      padding: isMobile ? '1.5rem' : '2.5rem',
      overflow: 'auto',
      margin: '0 auto',
    } as CSSProperties,
  };

  if (modalStore.data?.variant === 'fullscreen') {
    modalStyles.overlay.backgroundColor = theme.color.purple100;
    modalStyles.content.maxWidth = '100%';
    modalStyles.content.maxHeight = '100%';
    modalStyles.content.width = '100%';
    modalStyles.content.height = '100%';
    modalStyles.content.backgroundColor = theme.color.purple100;
    modalStyles.content.borderRadius = '0';
    modalStyles.content.border = 'none';
    modalStyles.content.padding = '0';
  }
  let closeOnOverlayOrEsc = true;

  const SpecificModal = modalType ? components[modalType] : undefined;

  if (modalProps?._container) {
    if (modalProps._container.width) {
      modalStyles.content.width = modalProps._container.width;
    }
    if (modalProps._container.autoWidth) {
      modalStyles.content.width = 'auto';
    }
    if (modalProps._container.minWidth) {
      modalStyles.content.minWidth = modalProps._container.minWidth;
    }
    if (typeof modalProps._container.closeOnOverlayClickOrEsc === 'boolean') {
      closeOnOverlayOrEsc = modalProps._container.closeOnOverlayClickOrEsc;
    }
  }

  const el = document.getElementById('root');
  if (!el) return null;

  return (
    <ReactModal
      style={modalStyles}
      isOpen={modalType !== null}
      contentLabel="Modal"
      onRequestClose={modalStore.hide}
      parentSelector={() => el}
      shouldCloseOnOverlayClick={closeOnOverlayOrEsc}
      shouldCloseOnEsc={closeOnOverlayOrEsc}
    >
      <div id="modal">
        {SpecificModal && <SpecificModal {...modalProps} closeModal={modalStore.hide} />}
      </div>
    </ReactModal>
  );
});

type ModalActionsProps = Omit<StackProps, 'space'> & { children: ReactNode };

type ModalContainerProps = {
  children: ReactNode;
};

export function Modal(props: ModalContainerProps) {
  const { children } = props;
  return (
    <Stack space={6} dataCy="modal-open">
      {children}
    </Stack>
  );
}

export const ModalActions = observer<ModalActionsProps>((props) => (
  <Stack orientation="horizontal" space={4} justify="flex-end" {...props} />
));

export const ModalHeader = observer(({ text, ...rest }: { text: string }) => {
  return <Text font="heading1" text={text} {...rest} />;
});

Modal.Actions = ModalActions;
Modal.Header = ModalHeader;
